import React, {
  memo,
  type ReactElement,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Badge,
  Box,
  ButtonIcon,
  ButtonSelect,
  Icon,
  type MenuOption,
  ToggleButton,
  ToggleButtonGroup,
  type ToggleButtonOption,
} from '@saviynt/design-system';
import cx from 'classnames';

import { testIds } from '../../../tests';
import { DEBOUNCE_TIME_FILTER_SEARCH } from '../../constants';
import { useInvertedLocalize } from '../../hooks';
import {
  type ActiveAdvancedFilterState,
  type AdvancedFilterState,
  type TabFilterState,
} from '../../types';

import SodAdvancedFilters from './SodAdvancedFilters';
import SodSearch from './SodSearch';

import './SodControls.css';

/**
 * Type for SodControls filters
 */
export type FilterState = Omit<
  AdvancedFilterState,
  'label' | 'options' | 'category'
>;

/**
 * Interface for SodControls shared funcs
 */
export interface SodAdvancedFiltersSharedFunc {
  handleFilterChange: (filter: FilterState) => void | false | undefined;
  handleOptionsClick: (
    key: string,
    method: string,
    selectedOptions: MenuOption[]
  ) => void | false | undefined;
  handleFilterSearch: (
    key: string,
    search: string
  ) => Promise<MenuOption[] | void | undefined | null | false>;
  handleClearBtn: () => void;
}

/**
 * Interface for SodControls component props
 */
interface SodControlsProps extends SodAdvancedFiltersSharedFunc {
  className?: string;
  tabs: TabFilterState[];
  filters: AdvancedFilterState[];
  activeFilters: ActiveAdvancedFilterState[];
  maxEnabledFilterCount?: number;
  searchPlaceholder: string;
  searchDebounce?: number;
  isClearBtn: boolean;
  isInsightsDisabled?: boolean;
  isInsightsOpen: boolean;
  currentMode: number;
  isDarkTheme?: boolean;
  setIsInsightsOpen: (state: boolean) => void | false | undefined;
  handleTabChange: (value: string) => void | false | undefined;
  handleModeChange: (state: number) => void | false | undefined;
  handleSearch: (state: string) => void | false | undefined;
  isDisabled?: boolean;
  isModeTogglerDisabled?: boolean;
}

/**
 * SodControls Component
 *
 * A comprehensive control panel component for managing SOD (Segregation of Duties)
 * violations and filters. It provides an interface for searching, filtering, and
 * viewing SOD violations in different modes.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS class names
 * @param {Array<TabFilterState>} props.tabs - Array of tab configurations
 * @param {Array<AdvancedFilterState>} props.filters - Array of filter configurations
 * @param {Array<ActiveAdvancedFilterState>} props.activeFilters - Array of the applied filter
 * @param {number} [props.maxEnabledFilterCount=5] - Maximum number of active filters to display
 * @param {string} [props.searchPlaceholder='Search your text here...'] - Placeholder text for search input
 * @param {number} [props.searchDebounce=300] - Debounce time in milliseconds for search input
 * @param {boolean} [props.isClearBtn=false] - Whether to show clear filters button
 * @param {boolean} [props.isInsightsDisabled] - Whether insights feature is disabled
 * @param {boolean} [props.isInsightsOpen=true] - Whether insights panel is open
 * @param {number} [props.currentMode=1] - Set the mode of the view
 * @param {boolean} [props.isDarkTheme=false] - Weather darktheme or not
 * @param {Function} props.setIsInsightsOpen - Callback to toggle insights panel
 * @param {Function} props.handleTabChange - Callback when active tab changes
 * @param {Function} props.handleModeChange - Callback when view mode changes
 * @param {Function} props.handleSearch - Callback when search input changes
 * @param {Function} props.handleFilterChange - Callback when filter selection changes
 * @param {Function} props.handleFilterSearch - Callback for filter search functionality
 * @param {Function} props.handleOptionsClick - Callback when filter options are clicked
 * @param {Function} props.handleClearBtn - Callback when clear filters button is clicked
 * @param {boolean} props.isDisabled - Flag to disable to all the filters
 * @param {boolean} props.isModeTogglerDisabled - Flag to disable mode toggler
 *
 * @example
 * ```tsx
 * <SodControls
 *   tabs={[
 *     { label: 'Active', value: 'active', count: 5 },
 *     { label: 'Archived', value: 'archived', count: 2 }
 *   ]}
 *   filters={[
 *     {
 *       key: 'status',
 *       label: 'Status',
 *       options: [
 *         { label: 'Active', value: 'active' },
 *         { label: 'Inactive', value: 'inactive' }
 *       ]
 *     }
 *   ]}
 *   searchPlaceholder="Search violations..."
 *   handleTabChange={(value) => console.log('Tab changed:', value)}
 *   handleSearch={(query) => console.log('Search query:', query)}
 *   handleFilterChange={(filter) => console.log('Filter changed:', filter)}
 * />
 * ```
 *
 * @features
 * - Tab-based navigation with badge support
 * - Advanced filtering system with multi-select options
 * - Debounced search functionality
 * - Toggle between different view modes (Analysis/List view)
 * - Insights panel integration
 * - Responsive search field that expands/collapses
 *
 * @states
 * - controlTabs: Manages active tab selection
 * - searchText: Manages search input value
 * - isSearchExpaded: Controls search field expansion state
 * - isFilterBarOpen: Controls visibility of advanced filters
 * - toggledViewSelection: Manages current view mode
 *
 * @dependencies
 * - @saviynt/design-system components
 * - React hooks (useState, useEffect)
 * - Custom hooks (useDebounceForFunctions)
 *
 * @accessibility
 * - Supports keyboard navigation
 * - Uses semantic HTML elements
 * - Includes proper ARIA attributes
 *
 * @performance
 * - Implements debounced search to prevent excessive API calls
 * - Uses React.memo for component memoization
 * - Optimized re-renders through proper state management
 *
 * @styling
 * - Supports custom className for styling overrides
 * - Uses CSS modules for style encapsulation
 * - Responsive design with mobile considerations
 */
function SodControls({
  className,
  tabs = [] as TabFilterState[],
  filters,
  activeFilters = [] as ActiveAdvancedFilterState[],
  maxEnabledFilterCount,
  searchPlaceholder = 'Search your text here...',
  searchDebounce = DEBOUNCE_TIME_FILTER_SEARCH,
  isClearBtn = false,
  isInsightsDisabled,
  isInsightsOpen = true,
  currentMode = 1,
  isDarkTheme,
  setIsInsightsOpen = () => {},
  handleTabChange,
  handleModeChange,
  handleSearch,
  handleFilterChange,
  handleFilterSearch,
  handleOptionsClick,
  handleClearBtn,
  isDisabled,
  isModeTogglerDisabled,
}: SodControlsProps): ReactElement {
  const [controlTabs, setControlTabs] = useState(1);
  const [isFilterBarOpen, setIsFilterBarOpen] = useState(false);
  const [toggledViewSelection, setToggledViewSelection] = useState(currentMode);

  const l = useInvertedLocalize();

  // Tab section
  const isTabAvailable = useMemo(
    () => Boolean(Array.isArray(tabs) && tabs.length > 0),
    [tabs]
  );

  useEffect(() => {
    if (isTabAvailable) {
      const defaultIndex = tabs.findIndex((tab) => tab.isInitial);

      setControlTabs(defaultIndex !== -1 ? defaultIndex + 1 : 1);
    }
  }, [tabs, isTabAvailable]);

  const tabEntries = Object.values(tabs);
  const tabOptions: ToggleButtonOption[] = tabEntries.map((tab) => ({
    value: tab.value,
    label: tab.label,
    badge: (
      <Badge
        kind='number'
        size='medium'
        badgeStyle='outlined'
        number={tab.count}
        color='secondary'
      />
    ),
    isDisabled,
  }));

  // Mode section
  useEffect(() => {
    setIsFilterBarOpen(false);
    handleModeChange(toggledViewSelection);
  }, [toggledViewSelection, handleModeChange]);

  // Mode toggler
  const handleModeToggler = (value: number) => {
    if (isDisabled ?? isModeTogglerDisabled) {
      return;
    }

    setToggledViewSelection(value);
  };

  useEffect(() => {
    setToggledViewSelection(currentMode);
  }, [currentMode]);

  const sodControlsClasses = cx('SodControls', className);

  return (
    <Box
      tag='div'
      className={sodControlsClasses}
      dataTestId={testIds.SOD_CONTROLS}>
      <Box className='SodControls-advancedFiltersWrapper'>
        {isTabAvailable && (
          <ToggleButtonGroup
            options={tabOptions}
            visibleCount={2}
            selection={controlTabs}
            setSelection={setControlTabs}
            onSelectionChange={handleTabChange}
          />
        )}
        <ButtonSelect
          label={l('Filter')}
          ariaLabel={l('Filter')}
          kind='rectangle'
          onClick={() => {
            setIsFilterBarOpen((prevStatus) => !prevStatus);
          }}
          isOpen={isFilterBarOpen}
          prefixIcon={<Icon kind='Filter' />}
          BadgeComp={
            isClearBtn ? (
              <Badge kind='dot' color='primary' size='small' />
            ) : null
          }
          className='SodControls-filterButton'
          dataTestId={testIds.SOD_CONTROLS_FILTER_BTN}
          isDisabled={isDisabled}
          isVisualCueEnabled={false}
        />
        <SodSearch
          searchPlaceholder={searchPlaceholder}
          inlineMesssage={l('Type more than 2 characters to see results.')}
          searchDebounce={searchDebounce}
          isDisabled={false}
          handleSearch={handleSearch}
        />
      </Box>
      <Box className='SodControls-modeToggler'>
        {!isInsightsOpen && (
          <ButtonIcon
            size='large'
            kind='outlinedNeutral'
            icon={<Icon kind='SaviCompanion' />}
            isDisabled={isInsightsDisabled ?? isDisabled}
            onClick={setIsInsightsOpen}
            ariaLabel={l('Open Risk SodInsights Aside')}
            dataTestId={testIds.SOD_CONTROLS_INSIGHTS_BTN}
            // BadgeComp={<Badge kind='dot' color='primary' size='small' />} // TODO: enable this If insights filter is applied
          />
        )}
        <ToggleButtonGroup
          selection={toggledViewSelection}
          setSelection={handleModeToggler}
          isPill>
          <ToggleButton
            icon={<Icon kind='Analyze' />}
            dataTestId={testIds.SOD_CONTROLS_MODE_1}
            isDisabled={isDisabled ?? isModeTogglerDisabled}
          />
          <ToggleButton
            icon={<Icon kind='List' />}
            dataTestId={testIds.SOD_CONTROLS_MODE_2}
            isDisabled={isDisabled ?? isModeTogglerDisabled}
          />
        </ToggleButtonGroup>
      </Box>
      <SodAdvancedFilters
        filters={filters}
        activeFilters={activeFilters}
        maxEnabledFilterCount={maxEnabledFilterCount}
        isClearBtn={isClearBtn}
        searchDebounce={searchDebounce}
        handleFilterChange={handleFilterChange}
        handleFilterSearch={handleFilterSearch}
        handleOptionsClick={handleOptionsClick}
        handleClearBtn={handleClearBtn}
        className={
          isFilterBarOpen
            ? 'SodAdvancedFilters--isOpen'
            : 'SodAdvancedFilters--isClose'
        }
        isDisabled={isDisabled}
        isDarkTheme={isDarkTheme}
      />
    </Box>
  );
}

SodControls.defaultProps = {
  className: '',
  maxEnabledFilterCount: 999,
  isInsightsDisabled: false,
  isDarkTheme: false,
  searchDebounce: DEBOUNCE_TIME_FILTER_SEARCH,
  isDisabled: false,
  isModeTogglerDisabled: false,
};

export default memo(SodControls);
